import type { ReactNode } from 'react';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import { useAppContext } from 'src/components/app-context';
import { Axis } from '@prisjakt/ui';
import { Hideable } from '@prisjakt/ui/hideable';
import { Panel } from '@prisjakt/ui/panel';
import { ScrollLink, ScrollElement } from 'src/components/scroll';
import type { ProductsSlice } from '../../../../types';
import CategoryInformation from '../../../category/components/category-information';
import type { LayoutMode } from '../../../../components/layout-buttons/types';
import { ICON_SIZE } from '../../../../components/layout-buttons/constants';
import Pagination from './pagination';
import SearchInput, { useSearchQuery } from './search-input';
import LayoutButtons from '../../../../components/layout-buttons';
import { ProductsHeader } from '../../../../components/products-header';
import formatNumber from '../../../../globals/helpers/format-number';
import OverlaySort from './overlay-sort';
import { ToggleButton, VariantEnum } from '../toggle-button';
import SortOptions from './sort-options';
import { NoResults } from 'src/components/no-results';
import * as S from './styles';
import { TooltipPlacement } from 'src/components/tooltip-new';
import { IconMagnifier } from '@prisjakt/ui/icon/magnifier';
import { IconSort } from '@prisjakt/ui/icon/sort';
import { IconFilter } from '@prisjakt/ui/icon/filter';
import { FilterContext } from '../../filter-context';
import { DesktopOnly, MediumDown } from 'src/ui/block';
import RouteContext from 'src/routes/context';
import { useLocation } from 'react-router';

type Props = Pick<
  ProductsSlice,
  | 'id'
  | 'pageInfo'
  | 'productsCount'
  | 'storeCount'
  | 'legalText'
  | 'availableSorts'
  | 'currentSort'
  | 'maxHitsAmount'
> & {
  children: ReactNode;
  layout: LayoutMode;
  onLayoutChange: (layout: LayoutMode) => void;
  compareProductsToggle?: ReactNode;
  availableSortsTranslations: Record<string, string>;
  supportedViewModes: ReadonlyArray<LayoutMode>;
};

const ProductOffersContainer = ({
  pageInfo,
  productsCount,
  availableSorts,
  availableSortsTranslations,
  currentSort,
  onLayoutChange,
  layout,
  storeCount,
  legalText,
  children,
  maxHitsAmount,
  compareProductsToggle,
  supportedViewModes,
}: Props) => {
  const { market, t } = useAppContext();
  const { dispatch } = useContext(RouteContext);
  const location = useLocation();
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const searchInputRef: React.RefObject<HTMLInputElement> = createRef();
  const [query] = useSearchQuery();
  const [showSearch, setShowSearch] = useState(query !== '');

  useEffect(
    () => setShowSearch(query !== '' || showSearch),
    [query, showSearch]
  );

  const sortClick = useCallback(
    (sort: string) => {
      dispatch({
        type: 'SORT_CHANGE',
        sort,
        location,
      });
    },
    [dispatch, location]
  );

  const paginationClick = useCallback(
    (clickedOffset: number | undefined) => {
      dispatch({
        type: 'PAGINATION',
        clickedOffset,
        location,
      });
    },
    [dispatch, location]
  );

  const onClickSearch = useCallback(() => {
    const visible = query !== '' || !showSearch;
    setShowSearch(visible);
    if (visible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [setShowSearch, showSearch, query, searchInputRef]);

  const headerElement = useMemo(
    () => (
      <ProductsHeader
        dataBotify="ProductCollectionProductCount"
        displayOptions={
          <>
            <DesktopOnly>{compareProductsToggle}</DesktopOnly>

            <DesktopOnly>
              <SearchInput />
            </DesktopOnly>

            <MediumDown>
              <S.SearchInputIcon
                data-test="SearchInputIcon"
                onClick={onClickSearch}
                iconSize={ICON_SIZE}
              >
                <S.Tooltip
                  placement={TooltipPlacement.LEFT}
                  title={t('category', 'product-search.placeholder')}
                >
                  <IconMagnifier />
                </S.Tooltip>
              </S.SearchInputIcon>
            </MediumDown>

            <LayoutButtons
              layoutModes={supportedViewModes}
              activeLayout={layout}
              setLayout={onLayoutChange}
            />
          </>
        }
        sortOptions={
          <DesktopOnly>
            <SortOptions
              onClick={sortClick}
              currentSort={currentSort}
              availableSorts={availableSorts}
              availableSortsTranslations={availableSortsTranslations}
            />
          </DesktopOnly>
        }
        title={t('category', 'products.count', {
          n: productsCount,
          count: formatNumber(productsCount, market.code),
        })}
      >
        <MediumDown>
          <Hideable collapse={Axis.Y} visible={showSearch} renderWhenHidden>
            <SearchInput
              alwaysOpen
              inputWidth="100%"
              inputRef={searchInputRef}
            />
          </Hideable>
        </MediumDown>
      </ProductsHeader>
    ),
    [
      availableSorts,
      availableSortsTranslations,
      compareProductsToggle,
      currentSort,
      layout,
      market.code,
      onClickSearch,
      onLayoutChange,
      productsCount,
      searchInputRef,
      showSearch,
      supportedViewModes,
      t,
      sortClick,
    ]
  );

  const footerElement = useMemo(
    () => (
      <ScrollLink to="Products">
        <Pagination
          pageInfo={pageInfo}
          total={Math.min(productsCount, maxHitsAmount)}
          onClick={paginationClick}
        />
      </ScrollLink>
    ),
    [pageInfo, productsCount, maxHitsAmount, paginationClick]
  );

  const toggleMobileSort = useCallback(() => {
    setMobileSortOpen(!mobileSortOpen);
  }, [mobileSortOpen]);

  const onClose = useCallback(() => {
    setMobileSortOpen(false);
  }, []);

  const showTooManyHitsNotice =
    productsCount > maxHitsAmount &&
    (pageInfo.offset || 0) + (pageInfo.limit || 0) >= maxHitsAmount;

  const { mobileFilterOpen, setMobileFilterOpen, count } =
    useContext(FilterContext);

  return (
    <ScrollElement name="Products">
      <S.Wrapper>
        {mobileSortOpen && (
          <MediumDown>
            <div>
              <OverlaySort
                currentSort={currentSort}
                onClose={onClose}
                sortOptions={availableSorts}
                sortTranslations={availableSortsTranslations}
              />
            </div>
          </MediumDown>
        )}

        <Panel>
          {headerElement}

          {productsCount === 0 && (
            <NoResults
              title={t('category', 'product-collection.no-results-title')}
            />
          )}
          {productsCount > 0 && (
            <MediumDown>
              <S.MobileWrapper>
                {/* filter */}
                <S.StyledFilterButton
                  count={count}
                  icon={<IconFilter size={18} />}
                  onToggle={() => setMobileFilterOpen(!mobileFilterOpen)}
                  title={t('filter', 'filter.title')}
                />

                <S.CompareSortWrapper>
                  {/* compare */}
                  {compareProductsToggle}

                  {/* sort */}
                  <ToggleButton
                    variant={VariantEnum.OUTLINED}
                    icon={<IconSort size={24} />}
                    onToggle={toggleMobileSort}
                    truncate
                    data-test="SortOrderButton"
                  />
                </S.CompareSortWrapper>
              </S.MobileWrapper>
            </MediumDown>
          )}
          {children}
          {showTooManyHitsNotice && (
            <S.Notice>
              {t('category', 'product-collection.too-many-hits-notice', {
                maxHitsAmount,
              })}
            </S.Notice>
          )}
          {footerElement}
          {legalText && (
            <CategoryInformation
              statistics={
                typeof storeCount === 'number'
                  ? {
                      productsCount,
                      storesCount: storeCount,
                    }
                  : undefined
              }
            />
          )}
        </Panel>
      </S.Wrapper>
    </ScrollElement>
  );
};

export default ProductOffersContainer;
